// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";

import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Video from "@sc/App/Components/Media/Video";
import { CustomAppState } from "@sc/App/Store/Reducers";
import { getProductVideosAndDocumentsState } from "@sc/App/Store/Selectors/ProductSelectors";

import React, { FC } from "react";
import { useSelector } from "react-redux";

const enum fields {
    maxNumberOfVideos = "maxNumberOfVideos",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.maxNumberOfVideos]: number;
    };
}

export interface ProductDetailsVideosStyles {
    wrapper?: InjectableCss;
    title?: TypographyPresentationProps;
    imageWrapper?: InjectableCss;
    videosContainer?: InjectableCss;
    videoContainer?: InjectableCss;
}

export const productDetailsVideosStyles: ProductDetailsVideosStyles = {};

const styles = productDetailsVideosStyles;

type Props = OwnProps & HasProductContext;

const ProductDetailsVideos: FC<Props> = ({ productContext, fields: { maxNumberOfVideos } }) => {
    const videos = useSelector(
        (state: CustomAppState) => getProductVideosAndDocumentsState(state, productContext.product.id).videos,
    );
    if (!videos.length) {
        return null;
    }
    return (
        <StyledWrapper {...styles.wrapper} data-test-selector="product-details-videos">
            <Typography {...styles.title}>{translate("Videos")}</Typography>
            <StyledWrapper {...styles.videosContainer}>
                {videos.slice(0, maxNumberOfVideos).map(video => (
                    <StyledWrapper {...styles.videoContainer} key={video.id}>
                        <StyledWrapper {...styles.imageWrapper}>
                            <Video
                                src={video.filePath}
                                controls
                            />
                        </StyledWrapper>
                    </StyledWrapper>
                ))}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: withProductContext(ProductDetailsVideos),
    definition: {
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
        fieldDefinitions: [
            {
                name: fields.maxNumberOfVideos,
                displayName: "Max Number Of Videos",
                editorTemplate: "IntegerField",
                defaultValue: 3,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
